"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "BaseUsersService", {
    enumerable: true,
    get: function() {
        return BaseUsersService;
    }
});
const _common = require("@nestjs/common");
const _nocodbsdk = require("nocodb-sdk");
const _uuid = require("uuid");
const _validator = /*#__PURE__*/ _interop_require_default(require("validator"));
const _helpers = require("~/helpers");
const _Noco = /*#__PURE__*/ _interop_require_default(require("~/Noco"));
const _apphooksservice = require("~/services/app-hooks/app-hooks.service");
const _catchError = require("~/helpers/catchError");
const _PagedResponse = require("~/helpers/PagedResponse");
const _stringHelpers = require("~/helpers/stringHelpers");
const _models = require("~/models");
const _globals = require("~/utils/globals");
const _extractProps = require("~/helpers/extractProps");
const _roleHelper = require("~/utils/roleHelper");
const _mailservice = require("~/services/mail/mail.service");
const _Mail = require("~/interface/Mail");
function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
function _interop_require_default(obj) {
    return obj && obj.__esModule ? obj : {
        default: obj
    };
}
function _ts_decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
function _ts_metadata(k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
}
let BaseUsersService = class BaseUsersService {
    async userList(context, param) {
        const baseUsers = await _models.BaseUser.getUsersList(context, {
            base_id: param.baseId,
            mode: param.mode
        });
        await _models.PresignedUrl.signMetaIconImage(baseUsers);
        return new _PagedResponse.PagedResponseImpl(baseUsers, {
            count: baseUsers.length
        });
    }
    async userInvite(context, param, ncMeta = _Noco.default.ncMeta) {
        (0, _helpers.validatePayload)('swagger.json#/components/schemas/ProjectUserReq', param.baseUser);
        if ((0, _roleHelper.getProjectRolePower)({
            base_roles: (0, _nocodbsdk.extractRolesObj)(param.baseUser.roles)
        }) > (0, _roleHelper.getProjectRolePower)(param.req.user)) {
            _catchError.NcError.forbidden(`Insufficient privilege to invite with this role`);
        }
        if (![
            _nocodbsdk.ProjectRoles.OWNER,
            _nocodbsdk.ProjectRoles.CREATOR,
            _nocodbsdk.ProjectRoles.EDITOR,
            _nocodbsdk.ProjectRoles.COMMENTER,
            _nocodbsdk.ProjectRoles.VIEWER,
            _nocodbsdk.ProjectRoles.INHERIT,
            _nocodbsdk.ProjectRoles.NO_ACCESS
        ].includes(param.baseUser.roles)) {
            _catchError.NcError.baseUserError('Invalid role');
        }
        const emails = (param.baseUser.email || '').toLowerCase().split(/\s*,\s*/).map((v)=>v.trim()).filter(Boolean);
        // check for invalid emails
        const invalidEmails = emails.filter((v)=>!_validator.default.isEmail(v));
        if (!emails.length) {
            return _catchError.NcError.baseUserError('Invalid email address');
        }
        if (invalidEmails.length) {
            _catchError.NcError.baseUserError('Invalid email address : ' + invalidEmails.join(', '));
        }
        const invite_token = (0, _uuid.v4)();
        const error = [];
        const reverseOrderedProjectRoles = [
            ..._nocodbsdk.OrderedProjectRoles
        ].reverse();
        const newRolePower = reverseOrderedProjectRoles.indexOf(param.baseUser.roles);
        // Check if current user has sufficient privilege to assign this role
        if (newRolePower > (0, _roleHelper.getProjectRolePower)(param.req.user)) {
            _catchError.NcError.forbidden(`Insufficient privilege to assign this role`);
        }
        for (const email of emails){
            // add user to base if user already exist (canonical lookup handles alias variants)
            const user = await _models.User.getByCanonicalEmail(email, ncMeta);
            const base = await _models.Base.get(context, param.baseId, ncMeta);
            if (!base) {
                return _catchError.NcError.baseNotFound(param.baseId);
            }
            if (user) {
                var _param_req;
                // check if this user has been added to this base
                const baseUser = await _models.BaseUser.get(context, param.baseId, user.id, ncMeta);
                const targetUser = baseUser && await _models.User.getWithRoles(context, user.id, {
                    user,
                    baseId: param.baseId,
                    workspaceId: context.workspace_id
                }, ncMeta);
                // if old role is owner and there is only one owner then restrict update
                if (targetUser && this.isOldRoleIsOwner(targetUser, base)) {
                    const baseUsers = await _models.BaseUser.getUsersList(context, {
                        base_id: param.baseId
                    }, ncMeta);
                    this.checkMultipleOwnerExist(baseUsers, base);
                    await this.ensureBaseOwner(context, {
                        baseUsers,
                        ignoreUserId: user.id,
                        baseId: param.baseId,
                        req: param.req
                    }, ncMeta);
                }
                // if already exists and has a role then throw error
                if ((baseUser == null ? void 0 : baseUser.is_mapped) && (baseUser == null ? void 0 : baseUser.roles)) {
                    _catchError.NcError.baseUserError(`${user.email} with role ${baseUser.roles} already exists in this base`);
                } else if (baseUser == null ? void 0 : baseUser.is_mapped) {
                    var _getProjectRole;
                    await _models.BaseUser.updateRoles(context, param.baseId, user.id, param.baseUser.roles, ncMeta);
                    await this.mailService.sendMail({
                        mailEvent: _Mail.MailEvent.BASE_ROLE_UPDATE,
                        payload: {
                            req: param.req,
                            user: user,
                            base: base,
                            oldRole: (_getProjectRole = (0, _roleHelper.getProjectRole)(baseUser)) != null ? _getProjectRole : this.getInheritedBaseRole({
                                base,
                                workspaceRole: baseUser == null ? void 0 : baseUser.workspace_roles
                            }),
                            newRole: param.baseUser.roles || 'editor'
                        }
                    }, ncMeta);
                } else {
                    var _param_req_user, _param_req1;
                    await _models.BaseUser.insert(context, {
                        base_id: param.baseId,
                        fk_user_id: user.id,
                        roles: param.baseUser.roles || 'editor',
                        invited_by: (_param_req1 = param.req) == null ? void 0 : (_param_req_user = _param_req1.user) == null ? void 0 : _param_req_user.id
                    }, ncMeta);
                    if (param == null ? void 0 : param.workspaceInvited) {
                        await this.mailService.sendMail({
                            mailEvent: _Mail.MailEvent.BASE_INVITE,
                            payload: {
                                req: param.req,
                                user: user,
                                base: base,
                                role: param.baseUser.roles || 'editor',
                                token: invite_token
                            }
                        }, ncMeta);
                    } else {
                        var _getProjectRole1;
                        await this.mailService.sendMail({
                            mailEvent: _Mail.MailEvent.BASE_ROLE_UPDATE,
                            payload: {
                                req: param.req,
                                user: user,
                                base: base,
                                oldRole: (_getProjectRole1 = (0, _roleHelper.getProjectRole)(baseUser)) != null ? _getProjectRole1 : this.getInheritedBaseRole({
                                    base,
                                    workspaceRole: baseUser == null ? void 0 : baseUser.workspace_roles
                                }),
                                newRole: param.baseUser.roles || 'editor'
                            }
                        }, ncMeta);
                    }
                }
                this.appHooksService.emit(_nocodbsdk.AppEvents.PROJECT_INVITE, {
                    base,
                    user,
                    role: param.baseUser.roles,
                    invitedBy: (_param_req = param.req) == null ? void 0 : _param_req.user,
                    req: param.req,
                    context
                });
            } else {
                try {
                    var _param_req_user1, _param_req2, _param_req3;
                    // create new user with invite token
                    const user = await _models.User.insert({
                        invite_token,
                        invite_token_expires: new Date(Date.now() + 24 * 60 * 60 * 1000),
                        email,
                        roles: _nocodbsdk.OrgUserRoles.VIEWER,
                        token_version: (0, _stringHelpers.randomTokenString)()
                    }, ncMeta);
                    // add user to base
                    await _models.BaseUser.insert(context, {
                        base_id: param.baseId,
                        fk_user_id: user.id,
                        roles: param.baseUser.roles,
                        invited_by: (_param_req2 = param.req) == null ? void 0 : (_param_req_user1 = _param_req2.user) == null ? void 0 : _param_req_user1.id
                    }, ncMeta);
                    this.appHooksService.emit(_nocodbsdk.AppEvents.PROJECT_INVITE, {
                        base,
                        user,
                        role: param.baseUser.roles,
                        req: param.req,
                        invitedBy: (_param_req3 = param.req) == null ? void 0 : _param_req3.user,
                        context
                    });
                    if (emails.length === 1) {
                        try {
                            await this.mailService.sendMail({
                                mailEvent: _Mail.MailEvent.BASE_INVITE,
                                payload: {
                                    req: param.req,
                                    user: user,
                                    base: base,
                                    role: param.baseUser.roles || 'editor',
                                    token: invite_token
                                }
                            }, ncMeta);
                        } catch (e) {
                            this.logger.error(e.message, e.stack);
                            return {
                                invite_token,
                                email
                            };
                        }
                    } else {
                        await this.mailService.sendMail({
                            mailEvent: _Mail.MailEvent.BASE_INVITE,
                            payload: {
                                req: param.req,
                                user: user,
                                base: base,
                                token: invite_token,
                                role: param.baseUser.roles || 'editor'
                            }
                        }, ncMeta);
                    }
                } catch (e) {
                    this.logger.error(e.message, e.stack);
                    if (emails.length === 1) {
                        _catchError.NcError.get(context).baseUserError('Bad Request');
                    } else {
                        error.push({
                            email,
                            error: e.message
                        });
                    }
                }
            }
        }
        if (emails.length === 1) {
            return {
                msg: 'The user has been invited successfully'
            };
        } else {
            return {
                invite_token,
                emails,
                error
            };
        }
    }
    getInheritedBaseRole({ base, workspaceRole }) {
        if (base == null ? void 0 : base.default_role) return base == null ? void 0 : base.default_role;
        if (workspaceRole !== null && workspaceRole !== undefined && typeof workspaceRole === 'object') {
            const wsRole = Object.keys(workspaceRole).filter((role)=>workspaceRole == null ? void 0 : workspaceRole[role])[0];
            return wsRole && _nocodbsdk.WorkspaceRolesToProjectRoles[wsRole];
        }
        return _nocodbsdk.WorkspaceRolesToProjectRoles[workspaceRole];
    }
    async baseUserUpdate(context, param, ncMeta = _Noco.default.ncMeta) {
        var _getProjectRole;
        (0, _helpers.validatePayload)('swagger.json#/components/schemas/ProjectUserUpdateReq', param.baseUser);
        if (!param.baseId) {
            _catchError.NcError.baseUserError('Missing base id');
        }
        const base = await _models.Base.get(context, param.baseId, ncMeta);
        if (!base) {
            return _catchError.NcError.baseNotFound(param.baseId);
        }
        if (![
            _nocodbsdk.ProjectRoles.OWNER,
            _nocodbsdk.ProjectRoles.CREATOR,
            _nocodbsdk.ProjectRoles.EDITOR,
            _nocodbsdk.ProjectRoles.COMMENTER,
            _nocodbsdk.ProjectRoles.VIEWER,
            _nocodbsdk.ProjectRoles.INHERIT,
            _nocodbsdk.ProjectRoles.NO_ACCESS
        ].includes(param.baseUser.roles)) {
            _catchError.NcError.baseUserError('Invalid role');
        }
        const user = await _models.User.get(param.userId, ncMeta);
        if (!user) {
            _catchError.NcError.baseUserError(`User with id '${param.userId}' doesn't exist`);
        }
        const targetUser = await _models.User.getWithRoles(context, param.userId, {
            user,
            baseId: param.baseId,
            workspaceId: base.fk_workspace_id
        }, ncMeta);
        if (!targetUser) {
            _catchError.NcError.baseUserError(`User with id '${param.userId}' doesn't exist in this base`);
        }
        // if old role is owner and there is only one owner then restrict update
        if (this.isOldRoleIsOwner(targetUser, base)) {
            const baseUsers = await _models.BaseUser.getUsersList(context, {
                base_id: param.baseId
            }, ncMeta);
            await this.checkMultipleOwnerExist(baseUsers, base);
            await this.ensureBaseOwner(context, {
                baseUsers,
                ignoreUserId: param.userId,
                baseId: param.baseId,
                req: param.req
            }, ncMeta);
        }
        const reverseOrderedProjectRoles = [
            ..._nocodbsdk.OrderedProjectRoles
        ].reverse();
        const newRolePower = reverseOrderedProjectRoles.indexOf(param.baseUser.roles);
        // Check if current user has sufficient privilege to assign this role
        if (newRolePower > (0, _roleHelper.getProjectRolePower)(param.req.user)) {
            _catchError.NcError.forbidden(`Insufficient privilege to assign this role`);
        }
        if ((0, _roleHelper.getProjectRolePower)(targetUser) > (0, _roleHelper.getProjectRolePower)(param.req.user)) {
            _catchError.NcError.forbidden(`Insufficient privilege to update user`);
        }
        const oldBaseUser = await _models.BaseUser.get(context, param.baseId, param.userId, ncMeta);
        if (oldBaseUser) {
            await _models.BaseUser.updateRoles(context, param.baseId, param.userId, param.baseUser.roles, ncMeta);
        } else {
            var _param_req_user, _param_req;
            await _models.BaseUser.insert(context, {
                base_id: param.baseId,
                fk_user_id: param.userId,
                roles: param.baseUser.roles,
                invited_by: (_param_req = param.req) == null ? void 0 : (_param_req_user = _param_req.user) == null ? void 0 : _param_req_user.id
            }, ncMeta);
        }
        await this.mailService.sendMail({
            mailEvent: _Mail.MailEvent.BASE_ROLE_UPDATE,
            payload: {
                req: param.req,
                user: user,
                base,
                oldRole: (_getProjectRole = (0, _roleHelper.getProjectRole)(targetUser)) != null ? _getProjectRole : this.getInheritedBaseRole({
                    base,
                    workspaceRole: targetUser == null ? void 0 : targetUser.workspace_roles
                }),
                newRole: param.baseUser.roles || 'editor'
            }
        }, ncMeta);
        this.appHooksService.emit(_nocodbsdk.AppEvents.PROJECT_USER_UPDATE, {
            base,
            user,
            baseUser: param.baseUser,
            oldBaseUser: oldBaseUser,
            req: param.req,
            context
        });
        return {
            msg: 'User has been updated successfully'
        };
    }
    /**
   * Checks if the user's current role is OWNER.
   * This considers both base roles and workspace roles.
   */ isOldRoleIsOwner(targetUser, base) {
        // Check if a base role is defined and if it includes the OWNER role.
        if (targetUser.base_roles) {
            const baseRole = (0, _roleHelper.getProjectRole)(targetUser);
            if (baseRole) {
                return baseRole === _nocodbsdk.ProjectRoles.OWNER;
            }
        }
        // Check if workspace_roles are present and if OWNER role is derived from them.
        if (!(base == null ? void 0 : base.default_role) && targetUser.workspace_roles) {
            var _extractRolesObj;
            return (_extractRolesObj = (0, _nocodbsdk.extractRolesObj)(targetUser.workspace_roles)) == null ? void 0 : _extractRolesObj[_nocodbsdk.WorkspaceUserRoles.OWNER];
        }
        // Return false if no OWNER role is found.
        return false;
    }
    /**
   * Ensures that at least one owner exists among the base users.
   * Throws a bad request error if no valid owner is found.
   */ checkMultipleOwnerExist(baseUsers, base) {
        const ownersCount = baseUsers.filter((u)=>{
            var _u_roles;
            // Check if the user has an explicit OWNER role in base roles.
            if ((_u_roles = u.roles) == null ? void 0 : _u_roles.includes(_nocodbsdk.ProjectRoles.OWNER)) return true;
            // If no base roles, check if the workspace role maps to an OWNER role.
            // if default role is set, it will be used as the user role
            if (!u.roles && u.workspace_roles) {
                // if default role assigned consider default role since workspace role will be overridden by default role
                if (base.default_role) {
                    // return false since `default_role` never be owner
                    return false;
                }
                return _nocodbsdk.WorkspaceRolesToProjectRoles[u.workspace_roles] === _nocodbsdk.ProjectRoles.OWNER;
            }
            return false;
        }).length;
        // Throw error if no valid owner is found.
        if (ownersCount <= 1) {
            _catchError.NcError.baseUserError('At least one owner is required');
        }
    }
    /**
   * Ensures that a base has an assigned owner.
   * If no direct owner is found, assigns ownership to the first user
   * whose role is derived from the workspace role.
   */ async ensureBaseOwner(context, { baseUsers, ignoreUserId, baseId, req, base: _base }, ncMeta = _Noco.default.ncMeta) {
        const base = _base || await _models.Base.get(context, baseId, ncMeta);
        // Check if at least one user (excluding ignored user) has an assigned OWNER role.
        const ownerUser = baseUsers.find((u)=>{
            var _u_roles;
            return u.id !== ignoreUserId && ((_u_roles = u.roles) == null ? void 0 : _u_roles.includes(_nocodbsdk.ProjectRoles.OWNER));
        });
        // If an owner exists, no further action is required.
        if (ownerUser) {
            return;
        }
        // Find the first user (excluding ignored user) with an OWNER role derived from workspace roles.
        const derivedOwner = baseUsers.find((u)=>u.id !== ignoreUserId && this.getInheritedBaseRole({
                base: base,
                workspaceRole: u.workspace_roles
            }) === _nocodbsdk.ProjectRoles.OWNER);
        // If no derived owner is found, return early.
        if (!derivedOwner) {
            return;
        }
        // Check if the baseUser already exists for the derived owner.
        const baseUser = await _models.BaseUser.get(context, baseId, derivedOwner.id, ncMeta);
        if (baseUser) {
            // Update the role to OWNER if the baseUser already exists.
            await _models.BaseUser.updateRoles(context, baseId, derivedOwner.id, _nocodbsdk.ProjectRoles.OWNER, ncMeta);
        } else {
            var _req_user;
            // Insert a new baseUser with OWNER role if it doesn't exist.
            await _models.BaseUser.insert(context, {
                base_id: baseId,
                fk_user_id: derivedOwner.id,
                roles: _nocodbsdk.ProjectRoles.OWNER,
                invited_by: req == null ? void 0 : (_req_user = req.user) == null ? void 0 : _req_user.id
            }, ncMeta);
        }
    }
    async baseUserDelete(context, param, ncMeta = _Noco.default.ncMeta) {
        var _param_req_user, _param_req_user_base_roles, _param_req_user1;
        const base_id = param.baseId;
        if (((_param_req_user = param.req.user) == null ? void 0 : _param_req_user.id) === param.userId) {
            _catchError.NcError.baseUserError("Admin can't delete themselves!");
        }
        const user = await _models.User.get(param.userId, ncMeta);
        const base = await _models.Base.get(context, base_id, ncMeta);
        if (!user) {
            _catchError.NcError.userNotFound(param.userId);
        }
        if (!((_param_req_user1 = param.req.user) == null ? void 0 : (_param_req_user_base_roles = _param_req_user1.base_roles) == null ? void 0 : _param_req_user_base_roles.owner)) {
            var _user_roles;
            if ((_user_roles = user.roles) == null ? void 0 : _user_roles.split(',').includes('super')) _catchError.NcError.forbidden('Insufficient privilege to delete a super admin user.');
        }
        const baseUser = await _models.User.getWithRoles(context, param.userId, {
            baseId: base_id,
            workspaceId: base.fk_workspace_id,
            user
        }, ncMeta);
        // check if user have access to delete user based on role power
        if ((0, _roleHelper.getProjectRolePower)(baseUser.base_roles) > (0, _roleHelper.getProjectRolePower)(param.req.user)) {
            _catchError.NcError.forbidden('Insufficient privilege to delete user');
        }
        // if old role is owner and there is only one owner then restrict to delete
        if (this.isOldRoleIsOwner(baseUser, base)) {
            const baseUsers = await _models.BaseUser.getUsersList(context, {
                base_id: param.baseId
            }, ncMeta);
            this.checkMultipleOwnerExist(baseUsers, base);
            await this.ensureBaseOwner(context, {
                baseUsers,
                ignoreUserId: param.userId,
                baseId: param.baseId,
                req: param.req
            }, ncMeta);
        }
        // block self delete if user is owner or super
        if (param.req.user.id === param.userId && param.req.user.roles.includes('owner')) {
            _catchError.NcError.badRequest("Admin can't delete themselves!");
        }
        await _models.BaseUser.delete(context, base_id, param.userId, ncMeta);
        this.appHooksService.emit(_nocodbsdk.AppEvents.PROJECT_USER_DELETE, {
            base,
            user,
            req: param.req,
            context
        });
        return true;
    }
    async baseUserInviteResend(context, param) {
        const user = await _models.User.get(param.userId);
        if (!user) {
            _catchError.NcError.baseUserError(`User with id '${param.userId}' not found`);
        }
        const base = await _models.Base.get(context, param.baseId);
        if (!base) {
            return _catchError.NcError.baseNotFound(param.baseId);
        }
        const invite_token = (0, _uuid.v4)();
        await _models.User.update(user.id, {
            invite_token,
            invite_token_expires: new Date(Date.now() + 24 * 60 * 60 * 1000)
        });
        const baseUser = await _models.BaseUser.get(context, param.baseId, user.id);
        const pluginData = await _Noco.default.ncMeta.metaGet2(context.workspace_id, context.base_id, _globals.MetaTable.PLUGIN, {
            category: _nocodbsdk.PluginCategory.EMAIL,
            active: true
        });
        if (!pluginData) {
            _catchError.NcError.baseUserError(`No Email Plugin is found. Please go to App Store to configure first or copy the invitation URL to users instead.`);
        }
        await this.mailService.sendMail({
            mailEvent: _Mail.MailEvent.BASE_INVITE,
            payload: {
                req: param.req,
                user: user,
                base: base,
                role: baseUser.roles || 'editor',
                token: invite_token
            }
        });
        this.appHooksService.emit(_nocodbsdk.AppEvents.PROJECT_USER_RESEND_INVITE, {
            base,
            user,
            baseUser: param.baseUser,
            req: param.req,
            context
        });
        return true;
    }
    async baseUserMetaUpdate(context, param) {
        // update base user data
        const baseUserData = (0, _extractProps.extractProps)(param.body, [
            'starred',
            'order',
            'hidden'
        ]);
        const base = await _models.Base.get(context, param.baseId);
        if (Object.keys(baseUserData).length) {
            var _param_user;
            const existingBaseUserData = await _models.BaseUser.get(context, param.baseId, (_param_user = param.user) == null ? void 0 : _param_user.id);
            // create new base user if it doesn't exist
            if (!(existingBaseUserData == null ? void 0 : existingBaseUserData.is_mapped)) {
                var _param_user1;
                await _models.BaseUser.insert(context, _extends({}, baseUserData, {
                    base_id: param.baseId,
                    fk_user_id: (_param_user1 = param.user) == null ? void 0 : _param_user1.id
                }));
                this.appHooksService.emit(_nocodbsdk.AppEvents.PROJECT_UPDATE, {
                    base: base,
                    updateObj: baseUserData,
                    oldBaseObj: _extends({}, base, existingBaseUserData),
                    user: param.user,
                    req: param.req,
                    context
                });
            } else {
                var _param_user2;
                await _models.BaseUser.update(context, param.baseId, (_param_user2 = param.user) == null ? void 0 : _param_user2.id, baseUserData);
                this.appHooksService.emit(_nocodbsdk.AppEvents.PROJECT_UPDATE, {
                    base: base,
                    updateObj: baseUserData,
                    oldBaseObj: _extends({}, base, existingBaseUserData),
                    user: param.user,
                    req: param.req,
                    context
                });
            }
        }
        return true;
    }
    isUserManagementRestricted(_params) {
    // placeholder for future logic
    }
    constructor(appHooksService, mailService){
        this.appHooksService = appHooksService;
        this.mailService = mailService;
        this.logger = new _common.Logger(BaseUsersService.name);
    }
};
BaseUsersService = _ts_decorate([
    (0, _common.Injectable)(),
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        typeof _apphooksservice.AppHooksService === "undefined" ? Object : _apphooksservice.AppHooksService,
        typeof _mailservice.MailService === "undefined" ? Object : _mailservice.MailService
    ])
], BaseUsersService);
